import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Divider, IconButton, TextField, Button, useMediaQuery, useTheme } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
    Elements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import EditIcon from '../../../assets/icons/Edit.png';
import CloseIcon from '../../../assets/icons/Close.png';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import PaymentApi from '../../../rest-api/payment-api';
import { useSnackbar } from '../SnackbarProvider';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import UserApi from '../../../rest-api/user-api';
import KlaviyoApi from '../../../rest-api/klaviyo-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import getDiscountByPlan from '../../helper/getDiscountHelper';
import mixpanel from 'mixpanel-browser';

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);

const stripeElementStyle = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#F9F9F9',
        '& fieldset': {
            border: 'none',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '16px',
        border: 0,
        color: 'black',
    },
};

const SettingsBilling = ({
    currentPaymentMethod,
    plan = null,
    editorActiveDefault = false,
    onPromoDiscountChange,
    showPromo,
    isShowOffer,
    isExpo,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [editorActive, setEditorActive] = useState(editorActiveDefault);
    const [cardholderName, setCardholderName] = useState('');
    const [billingAddress, setBillingAddress] = useState({
        line1: '',
        line2: '',
        city: '',
        postal_code: '',
        country: 'GB',
    });
    const [planDetails, setPlanDetails] = useState(plan);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const stripe = useStripe();
    const elements = useElements();
    const { authStore, userDataStore } = useContext(StoreContext);
    const showSnackbar = useSnackbar();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [promoCode, setPromoCode] = useState(); // removed 'LVS' code
    const [promoMessage, setPromoMessage] = useState(null);
    const [promoStatus, setPromoStatus] = useState(null);
    const [promoDiscount, setPromoDiscount] = useState(null); // Can be {type: 'percent', value: X} or {type: 'amount', value: Y, currency: 'gbp'}

    const [paymentRequest, setPaymentRequest] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (isShowOffer) {
            setPromoCode(''); // removed LVS
            handleValidatePromoCode();
        }
    }, [isShowOffer]);

    useEffect(() => {
        if (stripe && editorActiveDefault) {
            let discountedPrice = planDetails?.price || 0;

            // Calculate discounted price based on discount type
            if (promoDiscount?.type === 'percent') {
                discountedPrice = planDetails?.price - planDetails?.price * (promoDiscount.value / 100);
            } else if (promoDiscount?.type === 'amount') {
                discountedPrice = Math.max(0, planDetails?.price - promoDiscount.value);
            }

            console.warn('discountedPrice: ', discountedPrice);

            const pr = stripe.paymentRequest({
                country: 'GB',
                currency: 'gbp',
                total: {
                    label: 'Total',
                    amount: Math.round(discountedPrice) || 10,
                },
                requestPayerName: true,
                requestPayerEmail: true,
            });

            console.warn('pr start', pr);

            pr.canMakePayment().then((result) => {
                console.warn('pr then', pr);
                if (result) {
                    setPaymentRequest(pr);
                }
            });
        }
    }, [stripe, planDetails, promoDiscount]);

    useEffect(() => {
        const fetchDiscount = async () => {
            if (planDetails?.accountType) {
                const discount = await getDiscountByPlan(authStore.idToken, planDetails.accountType);
                console.warn('Fetched Discount for:', planDetails.accountType, 'is', discount);

                // getDiscountByPlan returns a percentage number, so wrap it in our new format
                const discountObj = discount ? { type: 'percent', value: discount } : null;
                setPromoDiscount(discountObj);
                onPromoDiscountChange(discountObj);
            }
        };

        if (planDetails) {
            fetchDiscount();
        }
    }, [planDetails]);

    const fetchPaymentMethod = async () => {
        try {
            const stripeCustomerId = userDataStore?.subscriptionData?.stripeCustomerId;
            if (stripeCustomerId) {
                const paymentDetails = await PaymentApi.getPaymentDetails(authStore.idToken, stripeCustomerId);
                console.warn('Customer payment details: ', paymentDetails);
                setPaymentDetails(paymentDetails);
            }
        } catch (error) {
            console.error('Error fetching payment method:', error);
        }
    };

    useEffect(() => {
        if (userDataStore && authStore) {
            fetchPaymentMethod();
        }
    }, [userDataStore, authStore]);

    useEffect(() => {
        if (plan) {
            setPlanDetails(plan);
        }
    }, [plan]);
    // TODO: this should be a helper as well, to use from many places, not only set up in setting-billing
    const handleValidatePromoCode = async () => {
        console.warn(`Applying promo code: ${promoCode}`);
        setPromoMessage(null);
        setPromoStatus(null);
        setPromoDiscount(null);
        const req = {
            promoCode: promoCode,
        };

        try {
            const validatePromoResponse = await PaymentApi.validatePromo(authStore.idToken, req);
            console.warn('Promo validation response:', validatePromoResponse);

            // Check if statusCode is 200 for success
            if (validatePromoResponse.statusCode === 200) {
                setPromoMessage('Promo code successfully applied!');
                setPromoStatus('success');

                const coupon = validatePromoResponse.promotionCode?.coupon;

                // Handle both percentage and fixed amount discounts
                let discount;
                if (coupon?.percent_off) {
                    // Percentage discount (e.g., 5% = 5)
                    discount = { type: 'percent', value: coupon.percent_off };
                } else if (coupon?.amount_off) {
                    // Fixed amount discount in pence, convert to pounds (e.g., 5688 pence = £56.88)
                    discount = { type: 'amount', value: coupon.amount_off / 100, currency: coupon.currency };
                } else {
                    discount = { type: 'none', value: 0 };
                }

                console.warn('Fetched discount:', discount);
                setPromoDiscount(discount);
                onPromoDiscountChange(discount);
            } else {
                setPromoMessage(validatePromoResponse.error || 'Failed to apply promo code.');
                setPromoStatus('error');
            }
        } catch (error) {
            console.error('Error during promo validation:', error);
            setPromoMessage('An error occurred while applying the promo code.');
            setPromoStatus('error');
        }
    };

    const handleBack = async () => {
        navigate(-1);
    };

    const handleConfirmSubscription = async () => {
        console.log('Subscription attempt with', plan);
        await handleSubmitPayment({ preventDefault: () => {} });
    };

    const handlePaymentRequest = async (event) => {
        event.preventDefault();

        if (!stripe || !paymentRequest) {
            return;
        }

        const { error } = await paymentRequest.show();
        if (error) {
            console.error('Error showing payment request:', error);
            showSnackbar('Failed to initiate payment request.', 'error');
        }
    };

    const handleSubmitPayment = async (event) => {
        event.preventDefault();
        setIsProcessing(true); // start spinner

        try {
            // Ensure stripe and elements are available
            if (!stripe || !elements) {
                throw new Error('Stripe has not loaded properly. Please refresh the page.');
            }

            const cardElement = elements.getElement(CardNumberElement);

            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: cardholderName,
                    address: billingAddress,
                    email: userDataStore.userData.emailAddress,
                    phone: userDataStore.userData.mobile,
                },
            });

            // Handle errors during payment method creation
            if (error) {
                console.error('Error creating payment method:', error);
                showSnackbar(error.message || 'An error occurred while processing your payment.', 'error');
                return; // Exit early to avoid sending the payment details to the backend
            }

            // Proceed to send payment details to the backend
            await sendPaymentDetailsToBackend(paymentMethod, plan);
        } catch (err) {
            console.error('Error during payment submission:', err);
            showSnackbar(err.message || 'Something went wrong during the payment process. Please try again.', 'error');
        } finally {
            // Stop spinner does not matter if success or failure
            setIsProcessing(false);
        }
    };

    const getPendingUpgradeHandoutId = () => {
        const getPendingId = (key) => {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            try {
                const data = JSON.parse(raw);
                const isRecent = Date.now() - data.timestamp < 3600000;
                if (!isRecent || !data.handoutId) {
                    localStorage.removeItem(key);
                    return null;
                }
                return data.handoutId;
            } catch (error) {
                localStorage.removeItem(key);
                return null;
            }
        };

        const pendingFromStorage =
            getPendingId('pendingCombinedHandoutSave') ||
            getPendingId('pendingCustomisedHandoutSave') ||
            getPendingId('pendingWhiteboardSave');

        if (pendingFromStorage) {
            return pendingFromStorage;
        }

        const pendingUpgradeHandoutId = sessionStorage.getItem('pendingUpgradeHandoutId');
        const pendingUpgradeSetAt = Number(sessionStorage.getItem('pendingUpgradeHandoutIdSetAt') || 0);
        const isRecent =
            pendingUpgradeHandoutId && pendingUpgradeSetAt > 0 && Date.now() - pendingUpgradeSetAt < 300000;

        if (!isRecent && pendingUpgradeHandoutId) {
            sessionStorage.removeItem('pendingUpgradeHandoutId');
            sessionStorage.removeItem('pendingUpgradeHandoutIdSetAt');
            return null;
        }

        return pendingUpgradeHandoutId;
    };

    const sendPaymentDetailsToBackend = async (paymentMethod, plan) => {
        try {
            const req = {
                userId: userDataStore.userId,
                paymentMethodId: paymentMethod.id,
                email: userDataStore.userData.emailAddress,
                ...(plan && { plan }),
                ...(promoStatus === 'success' && promoCode ? { promoCode } : {}),
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.updatePaymentDetails(bearerToken, req);

            console.warn('Payment details submission response: ', response);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            // Update customerId if returned
            if (response.customerId) {
                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    stripeCustomerId: response.customerId,
                };
            }

            if (response.requiresAction) {
                showSnackbar('Payment pending - please complete the security check with your bank', 'success');
                const result = await stripe.confirmCardPayment(response.paymentIntentClientSecret);
                if (result.error) {
                    showSnackbar('3D Secure authentication failed.', 'error');
                } else if (result.paymentIntent.status === 'succeeded') {
                    await confirmPaymentIntent(result.paymentIntent.id, response.subscriptionId);
                } else if (result.paymentIntent.status === 'requires_confirmation') {
                    await confirmPaymentIntent(result.paymentIntent.id, response.subscriptionId);
                }
            } else if (response.subscription) {
                // Handle subscription success
                const { id, status, items, current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: plan?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null, // Convert seconds to milliseconds
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('sub data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData?.accountType;
                const newPlan = planDetails?.accountType;
                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData: updatedSubscriptionData });
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData: updatedSubscriptionData,
                });
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    planDetails?.accountType,
                );
                if (previousPlan === 'Free' && newPlan === 'Professional') {
                    mixpanel.track('UpgradeFromFreeToProfessional', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (previousPlan === 'Free' && newPlan === 'Clinic') {
                    mixpanel.track('UpgradeFromFreeToClinic', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                }
                if (response.subscription) {
                    const pendingHandoutId = getPendingUpgradeHandoutId();
                    if (pendingHandoutId) {
                        showSnackbar(
                            'Your subscription details have been successfully updated. Open your saved handout to continue.',
                            'success',
                        );
                        return;
                    }
                    navigate('/', { state: { subscriptionSuccess: true } });
                } else {
                    showSnackbar('Billing details have been successfully updated.', 'success');
                }
            }
        } catch (error) {
            console.error('Error sending payment details to backend:', error);
            showSnackbar('Error when updating billing details, please try again.', 'error');
        }
    };

    const confirmPaymentIntent = async (paymentIntentId, subscriptionId) => {
        console.warn('Check this Payment Intent: ', paymentIntentId);
        try {
            const req = {
                paymentIntentId,
                userId: userDataStore.userId,
                subscriptionId: subscriptionId,
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.confirmPaymentDetails(bearerToken, req);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            // Update customerId if returned
            if (response.customerId) {
                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    stripeCustomerId: response.customerId,
                };
            }

            if (response.subscription) {
                const { id, status, items, current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: planDetails?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null,
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('subscription data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData?.accountType;
                const newPlan = planDetails?.accountType;
                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData: updatedSubscriptionData });
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData: updatedSubscriptionData,
                });
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    planDetails?.accountType,
                );
                if (previousPlan === 'Free' && newPlan === 'Professional') {
                    mixpanel.track('UpgradeFromFreeToProfessional', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (previousPlan === 'Free' && newPlan === 'Clinic') {
                    mixpanel.track('UpgradeFromFreeToClinic', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                }
                if (response.subscription) {
                    const pendingHandoutId = getPendingUpgradeHandoutId();
                    if (pendingHandoutId) {
                        showSnackbar(
                            'Your subscription details have been successfully updated. Open your saved handout to continue.',
                            'success',
                        );
                        return;
                    }
                    navigate('/', { state: { subscriptionSuccess: true } });
                }
                // Re-fetch payment methods to update state
                await fetchPaymentMethod();
            }
        } catch (error) {
            console.error('Error confirming payment intent:', error);
            showSnackbar('Error when confirming payment.', 'error');
        }
    };

    const renderStripeField = () => {
        const { card, billing_details } = paymentDetails?.paymentMethods?.[0] || {};

        return (
            <Box key="cardDetails" sx={{ position: 'relative' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Card number
                    </Typography>
                    {editorActive ? (
                        <Box
                            sx={{
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: '#f9f9f9',
                                height: '55px',
                            }}
                        >
                            <CardNumberElement />
                        </Box>
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                            **** **** **** {card?.last4 || '****'}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Name on card
                    </Typography>
                    {editorActive ? (
                        <TextField
                            variant="outlined"
                            fullWidth
                            value={cardholderName}
                            onChange={(e) => setCardholderName(e.target.value)}
                            placeholder="Cardholder name"
                            sx={stripeElementStyle}
                        />
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                            {billing_details?.name || '********'}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', mb: 2 }}>
                    <Box sx={{ flex: 1, mr: isMobile ? 0 : 2, mb: isMobile ? 3 : 0 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Expiration date
                        </Typography>
                        {editorActive ? (
                            <Box
                                sx={{
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: '#f9f9f9',
                                    maxWidth: '100%',
                                    width: '100%',
                                }}
                            >
                                <CardExpiryElement />
                            </Box>
                        ) : (
                            <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                                {card ? `${card.exp_month}/${card.exp_year}` : '**/**'}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            CVC
                        </Typography>
                        {editorActive ? (
                            <Box
                                sx={{
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: '#f9f9f9',
                                    maxWidth: '100%',
                                    width: '100%',
                                }}
                            >
                                <CardCvcElement />
                            </Box>
                        ) : (
                            <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                                ***
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Billing address
                    </Typography>
                    {editorActive ? (
                        <>
                            <TextField
                                label="Address line 1"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.line1}
                                onChange={(e) => setBillingAddress({ ...billingAddress, line1: e.target.value })}
                                sx={stripeElementStyle}
                            />
                            <TextField
                                label="Address line 2"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.line2}
                                onChange={(e) => setBillingAddress({ ...billingAddress, line2: e.target.value })}
                                sx={stripeElementStyle}
                            />
                            <TextField
                                label="City"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.city}
                                onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                                sx={stripeElementStyle}
                            />
                            <TextField
                                label="Postal code"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.postal_code}
                                onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value })}
                                sx={stripeElementStyle}
                            />
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                            {billing_details?.address?.line1 ? `${billing_details.address.line1}, ` : '********** '}
                            {billing_details?.address?.line2 ? `${billing_details.address.line2}, ` : ''}
                            {billing_details?.address?.city ? `${billing_details.address.city}, ` : '***** '}
                            {billing_details?.address?.postal_code
                                ? `${billing_details.address.postal_code} `
                                : '***** '}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Box className="settings-container">
            <Box
                className="settings-block"
                sx={{
                    backgroundColor: 'white',
                    borderRadius: 4,
                    padding: '16px 24px 16px 24px',
                    my: 3,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Billing details
                    </Typography>
                    {!editorActiveDefault && (
                        <IconButton onClick={() => setEditorActive(!editorActive)} sx={{ p: 0 }}>
                            {editorActive ? <img src={CloseIcon} alt="Close" /> : <img src={EditIcon} alt="Edit" />}
                        </IconButton>
                    )}
                    <Divider
                        sx={{
                            width: 'calc(100% + 48px)',
                            position: 'absolute',
                            bottom: 0,
                            left: '-24px',
                            backgroundColor: 'rgba(229, 229, 229, 1)',
                            display: editorActive ? 'none' : 'block',
                        }}
                    />
                </Box>

                {/* {paymentRequest && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6">Pay with Google Pay</Typography>
          <Box sx={{ maxWidth: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '200px', height: '60px' }}>
            <PaymentRequestButtonElement
              options={{ paymentRequest }}
              onClick={handlePaymentRequest}
              style={{
                paymentRequestButton: {
                  theme: 'dark', 
                  height: '60px',
                  width: '200px',
                },
              }}
            />
          </Box>
        </Box>
      )} */}

                {renderStripeField()}

                {editorActive && !editorActiveDefault && (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <PinkGradientButton className="black-btn" onClick={handleSubmitPayment} sx={{ my: 3 }}>
                            Save changes
                        </PinkGradientButton>
                    </Box>
                )}
            </Box>

            {location.pathname.includes('/checkout') && showPromo && (
                <Box
                    className="settings-block"
                    sx={{
                        backgroundColor: 'transparent',
                        borderRadius: 4,
                        padding: '16px 24px 16px 24px',
                        my: 3,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Promo code
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mx: -3 }}>
                        <TextField
                            placeholder="Enter code"
                            variant="outlined"
                            fullWidth
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            InputProps={{
                                sx: {
                                    paddingLeft: '16px',
                                },
                            }}
                        />

                        <PinkGradientButton onClick={handleValidatePromoCode} sx={{ marginLeft: 2 }}>
                            Apply
                        </PinkGradientButton>
                    </Box>
                    {promoMessage && (
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                            {promoStatus === 'success' ? (
                                <CheckCircleIcon style={{ color: 'green', marginRight: '8px' }} />
                            ) : (
                                <ErrorIcon style={{ color: 'red', marginRight: '8px' }} />
                            )}
                            <span style={{ color: promoStatus === 'success' ? 'green' : 'red' }}>
                                {promoMessage}{' '}
                                {promoDiscount?.type === 'percent' && promoDiscount.value === 25
                                    ? 'Enjoy 3 months off your plan!'
                                    : promoDiscount?.type === 'percent' && promoDiscount.value
                                      ? `Enjoy a ${promoDiscount.value}% discount on your plan!`
                                      : promoDiscount?.type === 'amount' && promoDiscount.value
                                        ? `Enjoy a £${promoDiscount.value.toFixed(2)} discount on your plan!`
                                        : ''}
                            </span>
                        </div>
                    )}
                </Box>
            )}

            {editorActive && editorActiveDefault && (
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box onClick={handleBack} sx={{ display: 'flex', alignItems: 'center', width: 120 }}>
                        <KeyboardBackspaceIcon />
                        <Button
                            sx={{
                                color: '#000453',
                                textDecoration: 'underline',
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            Go back
                        </Button>
                    </Box>
                    <PinkGradientButton onClick={() => setDialogOpen(true)}>Continue</PinkGradientButton>
                </Box>
            )}

            <ConfirmationDialog
                open={dialogOpen}
                handleClose={() => {
                    setDialogOpen(false);
                }}
                onConfirm={handleConfirmSubscription}
                title="Great choice! You're just one click away from more features!"
                message="Please confirm that you want to proceed with your upgrade. Your upgrade will be instant, and
      this will involve a pre-authorised payment."
                confirmText="Yes, upgrade"
                cancelText="No, cancel"
            />
            <BlockingSpinner open={isProcessing} />
        </Box>
    );
};

const SettingsBillingWrapper = (props) => {
    return (
        <Elements stripe={stripePromise}>
            <SettingsBilling {...props} />
        </Elements>
    );
};

export default SettingsBillingWrapper;
